import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectService } from '../../../project/project.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { InputSummary, InputSummaryItem } from '../../../models/input-summary';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ProjectPropName } from '../../../models/project/project.model';
import Utils from '../../../util';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-input-summary',
  templateUrl: './baseline-input-summary.component.html',
  styleUrls: ['./baseline-input-summary.component.scss']
})
export class BaselineInputSummaryComponent extends UserpilotReloader implements OnInit, OnDestroy, AfterViewInit, AnessaTranslationInterface {
  @ViewChild('outputsCheckbox') outputsCheckbox: MatCheckbox;
  onDestroyNotifier = new EventEmitter();
  inputs = {};
  inputSections: string[] = [];
  inputsDisplayNames = [];
  tableHeaders: string[];
  tableData: {}[] = [];
  selectedInputs: InputSummaryItem[] = [];
  Object = Object;

  projectServiceReady = false;
  unitServiceReady = false;
  private viewReady = false;
  trigger: string;
  private nameHeader: string;
  private valueHeader: string;
  private unitHeader: string;

  constructor(private projectCommService: ProjectCommunicationService,
              private unitService: UnitService,
              private projectService: ProjectService,
              private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    this.setTranslations();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.projectServiceReady = loaded;
      if (loaded) {
        this.setup();
      }
    });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(ready => {
        this.unitServiceReady = ready;
        if (ready) {
          this.setup();
        }
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        const oldSelections = Utils.deepCopy(this.selectedInputs);
        this.setTranslations();
        this.setup();
        this.restoreSelection(oldSelections);
        this.inputsChanged();
        this.trigger = language;
      });
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.setup();
  }

  setTranslations() {
    this.nameHeader = this.translationService.get('RESULTS.GENERAL.NAME');
    this.valueHeader = this.translationService.get('RESULTS.GENERAL.VALUE');
    this.unitHeader = this.translationService.get('RESULTS.GENERAL.UNIT');
    this.tableHeaders = [
      this.nameHeader,
      this.valueHeader,
      this.unitHeader
    ];
  }

  setup() {
    if (this.projectServiceReady && this.unitServiceReady && this.viewReady) {
      this.populateInputs();
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  checkAllInputs(checked: boolean) {
    if (checked) {
      this.selectedInputs = this.inputsDisplayNames;
    } else {
      this.selectedInputs = [];
    }
    this.populateTableData();
  }

  inputsChanged() {
    if (!this.selectedInputs || !this.inputsDisplayNames) return;
    this.outputsCheckbox.checked = this.selectedInputs.length === this.inputsDisplayNames.length;
    this.populateTableData();
  }

  populateInputs() {
    this.selectedInputs = [];
    this.inputsDisplayNames = [];
    const inputSummary = this.projectService.getResultProjectInputSummary();
    this.inputs = this.projectInputSummaryToResultsFormat(inputSummary);
    this.inputSections = Object.keys(this.inputs);
    for (const section of this.inputSections) {
      this.inputsDisplayNames.push(...this.inputs[section]);
    }
    this.convertInputSummaryUnits();
    this.inputsChanged();
  }

  private convertInputSummaryUnits() {
    for (const section of this.inputSections) {
      for (const input of this.inputs[section]) {
        if (input.unit) {
          const unit = this.unitService.getUnitById(input.unit);
          input.unit = unit.displayName;
        } else {
          input.unit = '-';
        }
      }
    }
  }

  private populateTableData() {
    this.tableData = [];
    if (this.selectedInputs && this.selectedInputs.length > 0) {
      for (const input of this.selectedInputs) {
        const tableItem = {};
        tableItem[this.nameHeader] = input.name;
        tableItem[this.valueHeader] = input.value;
        tableItem[this.unitHeader] = input.unit;
        this.tableData.push(tableItem);
      }
    }
  }

  private projectInputSummaryToResultsFormat(projSummary: {}) {
    const newFormat = {};
    if (projSummary[ProjectPropName.feedstock]) {
      const translation = this.translationService.get(ProjectPropName.feedstock);
      newFormat[translation] = this.convertInputSummaryToArray(
          projSummary[ProjectPropName.feedstock],
        translation,
        ProjectPropName.feedstock
        );
    }
    if (projSummary[ProjectPropName.plantSpecifications]) {
      const translation = this.translationService.get(ProjectPropName.plantSpecifications);
      newFormat[translation] = this.convertInputSummaryToArray(
          projSummary[ProjectPropName.plantSpecifications],
        translation,
        ProjectPropName.plantSpecifications
        );
    }
    if (projSummary[ProjectPropName.financial]) {
      const translation = this.translationService.get(ProjectPropName.financial);
      newFormat[translation] = this.convertInputSummaryToArray(
          projSummary[ProjectPropName.financial],
        translation,
        ProjectPropName.financial
        );
    }
    if (projSummary[ProjectPropName.ghgEmissions]) {
      const translation = this.translationService.get(ProjectPropName.ghgEmissions);
      newFormat[translation] =
        this.convertInputSummaryToArray(
          projSummary[ProjectPropName.ghgEmissions],
          translation,
          ProjectPropName.ghgEmissions
        );
    }
    return newFormat;
  }

  private convertInputSummaryToArray(summary: InputSummary, sectionName: string, sectionId: string) {
    const inputs: InputSummaryItem[] = [];
    inputs.push(...this.getInputs(summary, sectionName, sectionId));
    return inputs;
  }

  private getInputs(summary: InputSummary, prefix = '', idPrefix = '') {
    const inputs: InputSummaryItem[] = [];
    if (summary.inputs) {
      if (prefix) {
        for (const input of summary.inputs) {
          const processedName = this.processString(input.name);
          input.id = idPrefix + input.name;
          input.name = prefix + ', ' + processedName;
          input.value = this.processString(input.value);
        }
      }
      inputs.push(...summary.inputs);
    }
    for (const key of Object.keys(summary.subSection)) {
      const processedKey = this.processString(key);
      let loopPrefix = '';
      let loopIdPrefix = '';
      if (prefix) {
        loopPrefix = prefix + ', ' + processedKey;
        loopIdPrefix = idPrefix + key;
      } else {
        loopPrefix = processedKey;
        loopIdPrefix = key;
      }
      inputs.push(...this.getInputs(summary.subSection[key], loopPrefix, loopIdPrefix));
    }
    return inputs;
  }

  processString(sectionTitle: string) {
    // if the section title includes a period it's prob a translation id
    // if it does and it's not a translation it it will still be displayed normally
    // but there might be a console log, it's fine though :D
    if (sectionTitle.includes('.')) {
      return this.translationService.get(sectionTitle);
    }
    return sectionTitle;
  }

  private restoreSelection(oldSelections: InputSummaryItem[]) {
    for (const oldSelection of oldSelections) {
      for (const section of this.inputSections) {
        const foundItem = this.inputs[section].find(input => input.id === oldSelection.id);
        if (foundItem) {
          this.selectedInputs.push(foundItem);
        }
      }
    }
  }
}
